/**
 * customer-search.ts
 *
 * Single source of truth for "what fields make a Customer match a query".
 *
 * Used by BOTH:
 *   - the global search hook (`useGlobalSearch`) for ranked results, and
 *   - the customer-picker modals (DealCreationWizard, FaultIntakeModal,
 *     ManualJobCreationModal, …) for a simple boolean filter.
 *
 * Keeping the field list in one place means every customer-search surface in
 * the app searches the SAME comprehensive set — names, company/trading/ABN,
 * every phone, email, every saved address (legacy + multi-address), account
 * numbers, and external system IDs (Utilmate / Starlink / Pipedrive / Xero).
 *
 * If you add a searchable field to the Customer schema, add it here once and
 * every surface picks it up.
 */

import type { Customer, Vault } from '@/types/schema';
import {
  detectQueryShape,
  scoreFieldMatch,
  scorePhoneMatch,
  type QueryShape,
} from './search-ranking';

/**
 * Every searchable string inside a Site Vault — the WiFi network name +
 * password, the UniFi controller/site name, and the label + value of every
 * "other" credential (gate / alarm / router / NVR / portal …).
 *
 * The office deliberately wants the WHOLE vault searchable: UniFi site details
 * and device identifiers (e.g. the customer's NVR name) live here and must be
 * recallable from the search bar.
 *
 * Leak analysis — why indexing secrets here is safe: a vault match only ever
 * surfaces the CUSTOMER row, which prints just name / phone / address. The
 * vault value itself is NEVER rendered in a search result — it stays behind the
 * tap-to-reveal Site Vault card on the customer record (already fully visible
 * to the internal staff who use search). So indexing the full vault recalls the
 * right customer without exposing the credential in the results list. If a
 * result row is ever changed to echo the matched snippet, revisit this.
 */
export function vaultSearchFields(
  vault: Vault | undefined | null,
): Array<string | undefined | null> {
  if (!vault) return [];
  const fields: Array<string | undefined | null> = [
    vault.ssid,
    vault.pass,
    vault.unifiSiteName,
  ];
  for (const item of vault.other || []) {
    fields.push(item?.label, item?.value);
  }
  return fields;
}

/**
 * Every plain-text field on a customer worth matching against.
 * Phone fields are handled separately (digit-aware) — see customerPhoneFields.
 */
export function customerTextFields(c: Customer): Array<string | undefined | null> {
  const fields: Array<string | undefined | null> = [
    // Names
    c.name,
    c.firstName,
    c.lastName,
    // Company / grouping
    c.companyName,
    c.tradingName,
    c.parentCompanyName,
    c.primaryContactName,
    c.abn,
    // Email
    c.email,
    // Address (legacy single-address fields)
    c.address,
    c.addressLine2,
    c.city,
    c.state,
    c.postcode,
    c.zipCode,
    c.towerLocation,
    // Account / external identifiers
    c.accountNumber,
    c.utilmateId,
    c.starlinkAccount,
    c.xeroContactNumber,
    typeof c.pipedriveId === 'string' ? c.pipedriveId : undefined,
    // Classification (lets people search "VIP", "commercial", etc.)
    c.type,
    c.propertyType,
    c.serviceType,
  ];

  // Multi-address support — search every saved address.
  if (c.addresses && c.addresses.length > 0) {
    for (const a of c.addresses) {
      fields.push(a.label, a.address, a.addressLine2, a.city, a.state, a.postcode);
    }
  }

  // Site Vault — WiFi + UniFi site name + every "other" credential
  // (NVR / router / gate / alarm / portal). The office stores device
  // identifiers here (e.g. a customer's UniFi NVR name) and needs them
  // recallable from search. Whole-vault indexing is intentional — see
  // vaultSearchFields for why surfacing secrets here doesn't leak them.
  fields.push(...vaultSearchFields(c.vault));

  return fields;
}

/** Phone-shaped fields on a customer (digit-aware matching). */
export function customerPhoneFields(c: Customer): Array<string | undefined | null> {
  return [c.phone, c.phoneSecondary];
}

/**
 * Boolean match against a pre-computed query shape. Use this inside a filter
 * loop where the shape is computed once (see makeCustomerMatcher).
 */
export function matchesCustomerShape(c: Customer, shape: QueryShape): boolean {
  if (!shape.normalized) return false;

  // Phone-shaped query → digit-aware match so "0412 345 678" finds a record
  // whose phone is stored as "0412345678" (or vice versa), and a tail like
  // "345 678" still recalls the customer.
  if (shape.isPhone) {
    for (const p of customerPhoneFields(c)) {
      if (scorePhoneMatch(shape.digitsOnly, p) > 0) return true;
    }
  }

  // Text fields — plain case-insensitive substring.
  for (const f of customerTextFields(c)) {
    if (f && String(f).toLowerCase().includes(shape.normalized)) return true;
  }

  // Also allow a plain substring against phones for non-phone queries
  // (e.g. someone pastes a partial number with letters/spaces).
  const qDigits = shape.normalized.replace(/\D/g, '');
  if (qDigits.length >= 4) {
    for (const p of customerPhoneFields(c)) {
      if (p && p.replace(/\D/g, '').includes(qDigits)) return true;
    }
  }

  return false;
}

/**
 * Build a reusable predicate for filtering a customer list by a query.
 * Computes the query shape ONCE so the filter loop stays cheap even over
 * thousands of records.
 *
 *   const match = makeCustomerMatcher(searchText);
 *   const results = customers.filter(match);
 */
export function makeCustomerMatcher(rawQuery: string): (c: Customer) => boolean {
  const shape = detectQueryShape(rawQuery);
  if (!shape.normalized) return () => false;
  // Skip merged-duplicate stubs — they only exist to redirect via `mergedInto`,
  // so surfacing one in a picker would file the new record against a dead
  // customerId. Mirrors the guard global search already applies (useGlobalSearch).
  return (c: Customer) => !c.mergedInto && matchesCustomerShape(c, shape);
}

/** One-off boolean match (computes the shape each call). */
export function customerMatchesQuery(c: Customer, rawQuery: string): boolean {
  return matchesCustomerShape(c, detectQueryShape(rawQuery));
}

/**
 * Ranked base score for a customer against a query shape — the additive field
 * score plus a digit-aware phone score. Smart-query boosts (email/account
 * exact, phone-field bonus) are layered on by the caller (`useGlobalSearch`).
 */
export function scoreCustomerFields(c: Customer, shape: QueryShape): number {
  let score = 0;
  for (const f of customerTextFields(c)) score += scoreFieldMatch(shape.normalized, f);
  return score;
}
